import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Clock, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, canManage } from "@/contexts/AuthContext";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import { useGuidedTour } from "@/hooks/useGuidedTour";
import RotacionTurnos from "@/components/RotacionTurnos";

const TOUR_STORAGE_KEY = "chkr_tour_horarios_seen";

function tourSteps(user) {
  const steps = [];
  if (canManage(user)) {
    steps.push({ target: "[data-tour='horarios-nuevo']", title: "Crear un horario", content: "Define tipo de jornada (diurna, nocturna, mixta…), horas de entrada/salida, tolerancias, días laborables y si permite horas extra." });
  }
  steps.push({ target: "[data-tour='horarios-grid']", title: "Horarios configurados", content: "Cada tarjeta muestra la jornada, tolerancias y días de descanso. Los empleados se asignan a uno de estos horarios." });
  return steps;
}

const TIPOS = ["diurna", "nocturna", "vespertino", "mixta", "reducida", "especial", "rotativa", "flexible"];

// parseInt/parseFloat sobre un input vacío devuelve NaN, que al serializar viaja como
// null y el backend lo rechaza ("Input should be a valid integer"). Estos helpers lo
// degradan a 0 para que un campo que el usuario borró para reescribir no rompa el guardado.
const toInt = (v) => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : 0; };
const toFloat = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

// Un turno "cruza la medianoche" si tiene hora fija de entrada y salida y la de salida
// es <= la de entrada (p. ej. 16:00 -> 00:00). Debe coincidir con horario_nocturno.py.
const cruzaMedianoche = (f) => {
  if (f?.cruza_medianoche) return true;
  if (!f?.hora_entrada || !f?.hora_salida) return false;
  return f.hora_salida <= f.hora_entrada;
};

// Lapso de reloj entre entrada y salida (rueda a +24h si la salida es al día siguiente).
const lapsoTurno = (f) => {
  if (!f?.hora_entrada || !f?.hora_salida) return null;
  const [eh, em] = f.hora_entrada.split(":").map(Number);
  const [sh, sm] = f.hora_salida.split(":").map(Number);
  let mins = sh * 60 + sm - (eh * 60 + em);
  if (mins <= 0) mins += 24 * 60;
  return { h: Math.floor(mins / 60), m: mins % 60, cruza: cruzaMedianoche(f) };
};
const DIAS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const PERIODOS = [
  { value: "diario", label: "Diario", sufijo: "por día" },
  { value: "semanal", label: "Semanal", sufijo: "por semana" },
  { value: "quincenal", label: "Quincenal", sufijo: "por quincena" },
  { value: "mensual", label: "Mensual", sufijo: "por mes" },
];

export default function Horarios() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("horarios");
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const load = async () => {
    const [h, e] = await Promise.all([api.get("/horarios"), api.get("/empresas")]);
    setItems(h.data);
    setEmpresas(e.data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing(null);
    setForm({
      empresa_id: empresas[0]?.id || "", nombre: "", tipo: "diurna",
      hora_entrada: "09:00", hora_salida: "18:00", duracion_horas: 8,
      cruza_medianoche: false, gracia_cierre_horas: null,
      periodo_calculo: "diario", requiere_geolocalizacion: false,
      dias_laborables: [1, 2, 3, 4, 5], dias_descanso: [0, 6],
      tiempo_comida_min: 60, horas_por_dia: {}, tolerancia_entrada_min: 10, tolerancia_salida_min: 10,
      minutos_retardo: 10, minutos_falta: 60, permite_horas_extra: true,
    });
    setOpen(true);
  };
  const startEdit = (h) => { setEditing(h); setForm({ horas_por_dia: {}, ...h }); setOpen(true); };

  const toggleDia = (d) => {
    const lab = new Set(form.dias_laborables || []);
    if (lab.has(d)) lab.delete(d); else lab.add(d);
    const desc = [0, 1, 2, 3, 4, 5, 6].filter((x) => !lab.has(x));
    // Quita el override del día si deja de ser laborable, para no dejar horas huérfanas.
    const horasPorDia = { ...(form.horas_por_dia || {}) };
    if (!lab.has(d)) delete horasPorDia[d];
    setForm({ ...form, dias_laborables: Array.from(lab).sort(), dias_descanso: desc, horas_por_dia: horasPorDia });
  };

  const toggleOverrideDia = (d) => {
    const horasPorDia = { ...(form.horas_por_dia || {}) };
    if (horasPorDia[d]) delete horasPorDia[d];
    else horasPorDia[d] = { hora_entrada: form.hora_entrada || "09:00", hora_salida: form.hora_salida || "18:00", tiempo_comida_min: 0 };
    setForm({ ...form, horas_por_dia: horasPorDia });
  };

  const setOverrideDia = (d, campo, valor) => {
    const horasPorDia = { ...(form.horas_por_dia || {}) };
    horasPorDia[d] = { ...horasPorDia[d], [campo]: valor };
    setForm({ ...form, horas_por_dia: horasPorDia });
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/horarios/${editing.id}`, form);
      else await api.post("/horarios", form);
      toast.success(editing ? "Horario actualizado" : "Horario creado");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="horarios-page">
      <PageHeader
        kicker="Reglas operativas"
        title="Horarios y Jornadas"
        subtitle="Diurna, nocturna, mixta, especial. Tolerancias, retardos y reglas de horas extra."
        actions={
          <div className="flex gap-2 items-center">
            {tab === "horarios" && canManage(user) && (
              <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid="hor-new-btn" data-tour="horarios-nuevo">
                <Plus size={16} weight="bold" className="mr-2" /> Nuevo horario
              </Button>
            )}
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />

      <GuidedTour steps={tourSteps(user)} active={tourActive} onFinish={finishTour} />

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="horarios" data-testid="hor-tab-horarios">Horarios</TabsTrigger>
          <TabsTrigger value="turnos" data-testid="hor-tab-turnos">Turnos rotativos</TabsTrigger>
        </TabsList>
        <TabsContent value="turnos">
          <RotacionTurnos />
        </TabsContent>
        <TabsContent value="horarios">

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="horarios-grid">
        {items.length === 0 && <div className="md:col-span-2 lg:col-span-3 text-center text-slate-500 py-12 border border-dashed rounded-md">Sin horarios configurados</div>}
        {items.map((h) => (
          <div key={h.id} className="bg-white border border-slate-200 rounded-md p-5 hover:border-slate-300 transition-colors" data-testid={`hor-card-${h.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <Clock size={20} weight="duotone" className="text-slate-700" />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{h.tipo}</span>
                </div>
                <h3 className="font-display text-lg font-bold text-ink mt-1">{h.nombre}</h3>
              </div>
              {canManage(user) && <Button variant="ghost" size="sm" onClick={() => startEdit(h)}><PencilSimple size={16} /></Button>}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="label-uppercase">Entrada</div>
                <div className="font-mono font-bold text-ink">{h.hora_entrada || "—"}</div>
              </div>
              <div>
                <div className="label-uppercase">Salida</div>
                <div className="font-mono font-bold text-ink">
                  {h.hora_salida || "—"}
                  {cruzaMedianoche(h) && (
                    <span className="ml-1 text-[10px] font-sans font-semibold text-amber-700" title="La salida es del día siguiente a la entrada">
                      +1 día
                    </span>
                  )}
                </div>
              </div>
              <div>
                <div className="label-uppercase">Horas objetivo</div>
                <div className="font-bold text-ink">
                  {h.duracion_horas}h {PERIODOS.find((p) => p.value === (h.periodo_calculo || "diario"))?.sufijo}
                </div>
              </div>
              <div>
                <div className="label-uppercase">Comida</div>
                <div className="font-bold text-ink">{h.tiempo_comida_min}m</div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="label-uppercase mb-2">Días laborables</div>
              <div className="flex gap-1">
                {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                  <span
                    key={d}
                    className={`text-[10px] font-bold px-1.5 py-1 rounded w-8 text-center ${
                      h.dias_laborables?.includes(d) ? "bg-brand text-white" : "bg-slate-100 text-slate-400"
                    }`}
                  >{DIAS[d]}</span>
                ))}
              </div>
            </div>
            <div className="mt-3 text-[11px] text-slate-500">
              Tolerancia: {h.tolerancia_entrada_min}m · Retardo: {h.minutos_retardo}m · Falta: {h.minutos_falta}m
              {h.requiere_geolocalizacion && <> · <span className="text-slate-700 font-semibold">Geolocalización obligatoria</span></>}
            </div>
            {Object.keys(h.horas_por_dia || {}).length > 0 && (
              <div className="mt-2 text-[11px] text-slate-600">
                {Object.entries(h.horas_por_dia).sort(([a], [b]) => a - b).map(([d, o]) => (
                  <div key={d}>
                    <span className="font-semibold">{DIAS[d]}:</span> {o.hora_entrada || "—"}–{o.hora_salida || "—"}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Editar horario" : "Nuevo horario"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1 sm:col-span-2"><Label className="label-uppercase">Nombre</Label><Input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="hor-nombre-input" /></div>
            <div className="space-y-1">
              <Label className="label-uppercase">Tipo</Label>
              <Select value={form.tipo || "diurna"} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Periodo de cálculo</Label>
              <Select value={form.periodo_calculo || "diario"} onValueChange={(v) => setForm({ ...form, periodo_calculo: v })}>
                <SelectTrigger data-testid="hor-periodo-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PERIODOS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Horas objetivo ({PERIODOS.find((p) => p.value === (form.periodo_calculo || "diario"))?.sufijo})</Label>
              <Input type="number" step="0.5" value={form.duracion_horas ?? 0} onChange={(e) => setForm({ ...form, duracion_horas: toFloat(e.target.value) })} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Entrada (opcional)</Label>
              <Input type="time" value={form.hora_entrada || ""} onChange={(e) => setForm({ ...form, hora_entrada: e.target.value || null })} />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Salida (opcional)</Label>
              <Input type="time" value={form.hora_salida || ""} onChange={(e) => setForm({ ...form, hora_salida: e.target.value || null })} />
              {cruzaMedianoche(form) && (
                <p className="text-[11px] font-semibold text-amber-700">Es del día siguiente a la entrada</p>
              )}
            </div>
            {form.hora_entrada && form.hora_salida && (() => {
              const l = lapsoTurno(form);
              return (
                <div className="sm:col-span-2 -mt-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                  <span className="font-semibold text-slate-700">Turno: </span>
                  <span className="font-mono">{form.hora_entrada}</span> →{" "}
                  <span className="font-mono">{form.hora_salida}</span>
                  <span className={l.cruza ? "text-amber-700 font-semibold" : "text-slate-500"}>
                    {" "}{l.cruza ? "del día siguiente" : "(mismo día)"}
                  </span>
                  <span className="text-slate-500"> · {l.h} h{l.m ? ` ${l.m} m` : ""} de reloj entre marcajes (comida aparte)</span>
                </div>
              );
            })()}
            <div className="sm:col-span-2 text-xs text-slate-500 -mt-1">
              Deja Entrada y Salida vacías para un horario flexible sin hora fija: solo se contabilizarán las horas laboradas contra la meta del periodo elegido.
            </div>
            <div className="sm:col-span-2 flex items-start gap-3 rounded-md bg-slate-50 border border-slate-200 p-3">
              <Switch
                checked={cruzaMedianoche(form)}
                disabled={!!form.hora_entrada && !!form.hora_salida && form.hora_salida <= form.hora_entrada}
                onCheckedChange={(v) => setForm({ ...form, cruza_medianoche: v })}
                data-testid="hor-cruza-medianoche-switch"
              />
              <div>
                <Label>La jornada termina al día siguiente (turno nocturno / vespertino)</Label>
                <p className="text-xs text-slate-500 mt-1">
                  {form.hora_entrada && form.hora_salida && form.hora_salida <= form.hora_entrada
                    ? `La salida (${form.hora_salida}) cae al día siguiente de la entrada (${form.hora_entrada}). Ejemplo: quien entra el lunes ${form.hora_entrada} y sale el martes ${form.hora_salida}, sus horas cuentan para la jornada del lunes.`
                    : "Actívalo si el turno abre un día y cierra el siguiente sin una hora de salida menor a la de entrada (p. ej. horario flexible nocturno). Así la salida de la madrugada se registra en la jornada correcta."}
                </p>
                {cruzaMedianoche(form) && (
                  <div className="mt-3 flex items-center gap-2">
                    <Label className="label-uppercase whitespace-nowrap">Gracia de cierre (h)</Label>
                    <Input
                      type="number" step="0.5" min="0" className="w-24"
                      placeholder="6"
                      value={form.gracia_cierre_horas ?? ""}
                      onChange={(e) => setForm({ ...form, gracia_cierre_horas: e.target.value === "" ? null : toFloat(e.target.value) })}
                      data-testid="hor-gracia-cierre-input"
                    />
                    <span className="text-[11px] text-slate-500">
                      Horas tras la salida programada en que aún se puede cerrar el turno desde el Portal. Vacío = 6 h.
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-1"><Label className="label-uppercase">Tiempo comida (min)</Label><Input type="number" value={form.tiempo_comida_min ?? 0} onChange={(e) => setForm({ ...form, tiempo_comida_min: toInt(e.target.value) })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Tolerancia entrada (min)</Label><Input type="number" value={form.tolerancia_entrada_min ?? 0} onChange={(e) => setForm({ ...form, tolerancia_entrada_min: toInt(e.target.value) })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Min. retardo</Label><Input type="number" value={form.minutos_retardo ?? 0} onChange={(e) => setForm({ ...form, minutos_retardo: toInt(e.target.value) })} /></div>
            <div className="space-y-1"><Label className="label-uppercase">Min. falta</Label><Input type="number" value={form.minutos_falta ?? 0} onChange={(e) => setForm({ ...form, minutos_falta: toInt(e.target.value) })} /></div>
            <div className="sm:col-span-2">
              <Label className="label-uppercase mb-2 block">Días laborables</Label>
              <div className="flex gap-2 flex-wrap">
                {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                  <button
                    key={d} type="button" onClick={() => toggleDia(d)}
                    className={`px-3 py-2 text-xs font-bold rounded ${form.dias_laborables?.includes(d) ? "bg-brand text-white" : "bg-slate-100 text-slate-400"}`}
                  >{DIAS[d]}</button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2 border-t border-slate-100 pt-3">
              <Label className="label-uppercase mb-1 block">Horas distintas por día (opcional)</Label>
              <p className="text-xs text-slate-500 mb-2">
                Útil cuando un día laborable tiene entrada/salida distintas al resto (p. ej. sábado con jornada más corta).
              </p>
              <div className="space-y-2">
                {(form.dias_laborables || []).sort().map((d) => {
                  const override = form.horas_por_dia?.[d];
                  return (
                    <div key={d} className="border border-slate-200 rounded-md p-3">
                      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <input
                          type="checkbox" checked={!!override} onChange={() => toggleOverrideDia(d)}
                        />
                        {DIAS[d]}: horas distintas
                      </label>
                      {override && (
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          <div className="space-y-1">
                            <Label className="label-uppercase text-[10px]">Entrada</Label>
                            <Input type="time" value={override.hora_entrada || ""} onChange={(e) => setOverrideDia(d, "hora_entrada", e.target.value)} />
                          </div>
                          <div className="space-y-1">
                            <Label className="label-uppercase text-[10px]">Salida</Label>
                            <Input type="time" value={override.hora_salida || ""} onChange={(e) => setOverrideDia(d, "hora_salida", e.target.value)} />
                          </div>
                          <div className="space-y-1">
                            <Label className="label-uppercase text-[10px]">Comida (min)</Label>
                            <Input type="number" value={override.tiempo_comida_min ?? 0} onChange={(e) => setOverrideDia(d, "tiempo_comida_min", parseInt(e.target.value) || 0)} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {(form.dias_laborables || []).length === 0 && (
                  <p className="text-xs text-slate-400">Marca primero los días laborables arriba.</p>
                )}
              </div>
            </div>
            <div className="sm:col-span-2 flex items-center gap-3">
              <Switch checked={!!form.permite_horas_extra} onCheckedChange={(v) => setForm({ ...form, permite_horas_extra: v })} />
              <Label>Permite horas extra</Label>
            </div>
            <div className="sm:col-span-2 flex items-center gap-3">
              <Switch checked={!!form.requiere_geolocalizacion} onCheckedChange={(v) => setForm({ ...form, requiere_geolocalizacion: v })} data-testid="hor-requiere-geo-switch" />
              <Label>Requiere geolocalización para checar (entrada, descansos y salida)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="hor-save-btn">
              {busy ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
